/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, Grid, Stack, SvgIcon, useMediaQuery, useTheme, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ManageRetailersBox from './components/ManageRetailersBox';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useContext, useEffect, useState } from 'react';
import useLogError from '../../../hooks/useLogError';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Plus, Download01, ArrowUp } from '../../../assets';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { useNavigate } from 'react-router-dom';
import RetailerListMap from './components/RetailerListMap';
import { RetailerListModel, RetailerHeaderModel, UserFranchiseList, RetailerImportResult } from '../../../app/services/api/ofdClient';
import { UserContext } from '../../../components/shared/useUser';

const ManageRetailers = () => {
  const { getRetailersList, exportRetailers, importRetailers } = useNswagClient();
  const [retailers, setRetailers] = useState<RetailerHeaderModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation('manageRetailers');
  const { logError } = useLogError();
  const navigate = useNavigate();
  const [transformedData, setTransformedData] = useState<RetailerHeaderModel[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { user, hasPermissionTo, selectedFranchise } = useContext(UserContext);
  const [page, setPage] = useState(0);
  const [regionTab, setRegionTab] = useState<'my' | 'other'>('my');

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadResult, setUploadResult] = useState<RetailerImportResult | null>(null);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  const getUserFranchiseList = (otherRegions: boolean): UserFranchiseList => ({
    franchiseNames: (user?.franchises?.map(f => f.franchiseName).filter((name): name is string => !!name)) ?? [],
    otherRegions,
    currentFranchiseName: selectedFranchise?.franchiseName ?? '',
  });

  useEffect(() => {
    loadRetailerData(regionTab === 'other');
  }, [regionTab]);


  const loadRetailerData = (otherRegions = false) => {
    setIsLoading(true);
    setPage(0);
    getRetailersList(getUserFranchiseList(otherRegions)).then((result: RetailerListModel) => {
      if (result.retailerList) {
        setRetailers(result.retailerList as RetailerHeaderModel[] ?? []);
        setTransformedData(result.retailerList as RetailerHeaderModel[] ?? []); // update map data as well
      }
    })
      .catch((error) => {
        logError(error);
      })
      .finally(() => (setIsLoading(false)));
  };

  const handleDownloadClick = () => {
    setIsDownloading(true);
    exportRetailers(getUserFranchiseList(regionTab === 'other'))
      .then((response) => {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName || 'retailers.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        logError(error);
        setMessage(t('downloadErrorMessage'));
        setIsSuccess(false);
        setShowMessage(true);
      })
      .finally(() => setIsDownloading(false));
  };

  const handleUploadClick = () => {
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadDialog(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUpload = () => {
    if (!uploadFile) {
      return;
    }
    setIsUploading(true);

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const fileDataBase64 = dataUrl.split(',')[1];

      importRetailers({ fileDataBase64, franchiseNames: getUserFranchiseList(regionTab === 'other') })
        .then((importResult: RetailerImportResult) => {
          setUploadResult(importResult);
          setMessage(t('uploadSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          loadRetailerData(regionTab === 'other');
        })
        .catch((error) => {
          logError(error);
          setMessage(t('uploadErrorMessage'));
          setIsSuccess(false);
          setShowMessage(true);
        })
        .finally(() => setIsUploading(false));
    };
    reader.onerror = () => {
      setIsUploading(false);
      setMessage(t('uploadErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    };
    reader.readAsDataURL(uploadFile);
  };

  return <PageArea>
    <Grid container>
      <Stack width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
        }}>
        <PageTitle subTitle={t('subtitle')}>{t('heading')}</PageTitle>
        <Stack direction={isMobile ? 'column' : 'row'}
          spacing={2}>
          {hasPermissionTo(['RetailersView']) &&
            <Button variant="secondary"
              size="lg"
              startIcon={<SvgIcon><Download01 /></SvgIcon>}
              disabled={isDownloading}
              onClick={handleDownloadClick}>
              {isDownloading ? t('downloading') : t('download')}
            </Button>
          }
          {hasPermissionTo(['RetailersCreate']) &&
            <Button variant="secondary"
              size="lg"
              startIcon={<SvgIcon><ArrowUp /></SvgIcon>}
              onClick={handleUploadClick}>
              {t('upload')}
            </Button>
          }
          <Button variant="primary"
            size="lg"
            startIcon={<SvgIcon> <Plus fill='white' /> </SvgIcon>}
            disabled={!hasPermissionTo(['RetailersCreate'])}
            onClick={() => navigate('/retailers/0')}>
            {t('newRetailer')}
          </Button>
        </Stack>
      </Stack>
      {hasPermissionTo(['RetailersViewOtherRegion']) && 
      <Box sx={{ width: '100%', mb: 1, mt: 2 }}>
        <Tabs
          value={regionTab}
          onChange={(_, v) => setRegionTab(v)}
          aria-label="region tabs"
        >
          <Tab label={t('myRegions')}
            value="my" />
          <Tab label={t('otherRegions')}
            value="other" />
        </Tabs>
      </Box>
      }
      <Box sx={{ width: '100%', height: '400px', pt: '12px' }}>
        <RetailerListMap
          retailers={transformedData} />
      </Box>
      <ManageRetailersBox
        onCreate={() => navigate('/retailers/0')}
        retailers={retailers}
        transformedData={transformedData}
        setTransformedData={(retailers: RetailerHeaderModel[]) => setTransformedData(retailers)}
        loadData={() => loadRetailerData(regionTab === 'other')}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
      />
    </Grid>
    <Dialog open={showUploadDialog}
      onClose={() => setShowUploadDialog(false)}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>{t('upload')} {t('heading')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography>{t('uploadInstructions')}</Typography>
          <Box>
            <input aria-hidden
              accept=".csv"
              style={{ display: 'none' }}
              id="upload-retailers-file"
              type="file"
              onChange={handleFileChange} />
            <label htmlFor="upload-retailers-file">
              <Button component="span"
                variant="secondary"
                sx={{ mr: 2 }}>
                {t('chooseFile')}
              </Button>
            </label>
            <Typography component="span">
              {uploadFile ? uploadFile.name : t('noFileSelected')}
            </Typography>
          </Box>
          {uploadResult &&
            <Box sx={{ mt: 2 }}>
              <Typography>{t('uploadResults')}</Typography>
              <Typography>{t('created')}: {uploadResult.retailersCreated ?? 0}</Typography>
              <Typography>{t('updated')}: {uploadResult.retailersUpdated ?? 0}</Typography>
              {uploadResult.errors && uploadResult.errors.length > 0 &&
                <Box sx={{ mt: 1 }}>
                  <Typography color="error">{t('errors')}:</Typography>
                  {uploadResult.errors.map((error, index) => (
                    <Typography key={index}
                      color="error">
                      {error}
                    </Typography>
                  ))}
                </Box>
              }
            </Box>
          }
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowUploadDialog(false)}>
          {t('cancel')}
        </Button>
        <Button onClick={handleUpload}
          variant="primary"
          disabled={!uploadFile || isUploading}>
          {isUploading ? t('uploading') : t('upload')}
        </Button>
      </DialogActions>
    </Dialog>
    <MessagesSnackbar open={showMessage}
      onClose={() => setShowMessage(false)}
      message={message}
      severity={isSuccess ? 'success' : 'error'}
      duration={5000} />
  </PageArea>;
};

export default ManageRetailers;